import React, { Component } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router";
import { Card, Form, Button, Input } from "antd";
import { withRouter } from "react-router";
import DateRangePicker from "../../common/date-range-picker";
import MultiSelect from "../../common/multi-select";
import FullTextHelp from "../../common/full-text-help";
import { isEqual, isArray } from "lodash";
import { keepOnlyRepo3GPPQueryStrings } from "../../../helpers/url";
import { getDistinctValues } from "../../../services/api-repo";

const propsToStates = q => ({
  fullText: q.full_text,
  tdoc: q.tdoc,
  source: q.source,
  type: q.type,
  for: q.for,
  meetingDate: q.meeting_date,
  tdocStatus: q.tdoc_status,
  meeting: q.meeting,
  contact: q.contact,
  standard: q.spec_standard,
  technicalCategory: q.is_user_equipment && !isArray(q.is_user_equipment) ? [q.is_user_equipment] : q.is_user_equipment,
  specGroup: q.spec_group,
  specIPR: q.spec_initial_planned_release,
  specSeriesNo: q.spec_series_no,
  specType: q.spec_type,
  specNo: q.spec_spec_no
});

class Toolbar3GPP extends Component {
  static propTypes = {
    accessLevel: PropTypes.string,
    esUrl: PropTypes.string
  };
  state = {
    ...propsToStates(keepOnlyRepo3GPPQueryStrings(this.props.location.query))
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!isEqual(this.props.location.query, nextProps.location.query)) {
      this.setState(propsToStates(keepOnlyRepo3GPPQueryStrings(nextProps.location.query)));
    }
  }

  render() {
    const { accessLevel } = this.props;
    const fullTextLabel = (
      <span>
        {"FULL TEXT "}
        <FullTextHelp />
      </span>
    );
    const resetButton = (
      <Button size="small" onClick={this._resetFilter}>
        {"Reset"}
      </Button>
    );
    const toolbarTitle = (
      <div>
        <p style={{ margin: 0 }}>3GPP</p>
        <p style={{ margin: 0 }}>Standardization</p>
      </div>
    );

    return (
      <Card title={toolbarTitle} extra={resetButton}>
        <Form layout="vertical">
          <Form.Item label={fullTextLabel}>
            <Input
              placeholder="Full Text Search"
              onChange={e => this.setState({ fullText: e.target.value })}
              onPressEnter={this._updateRouter}
              value={this.state.fullText}
              disabled={accessLevel !== "full"}
            />
            {accessLevel !== "full" ? <Link to={{ pathname: "/standards/open/3gpp", query: { full_text: "example" } }}> {"Example"} </Link> : null}
          </Form.Item>
          {this._renderSelectFormItem("standard", "STANDARDS", false, "e.g. 4G", ["2G", "3G", "4G", "5G"])}
          {this._renderSelectFormItem("technicalCategory", "TECHNICAL CATEGORY", false, "e.g. User Equipment", ["true", "false"], val => val === "true" ? "User Equipment" : "Infrastructure")}
          {this._renderSelectFormItem("tdoc", "DOC ID", false, "e.g. 3BF-170015")}
          {this._renderSelectFormItem("source", "SOURCE", false, "e.g. Ericsson")}
          {this._renderSelectFormItem("type", "TYPE", false, "e.g. CR")}
          {this._renderSelectFormItem("meeting", "MEETING", false, "e.g. RAN2#104 (2018-11-12 - Spokane(us))")}
          {this._renderSelectFormItem("for", "FOR", false, "e.g. Decision")}
          {this._renderSelectFormItem("contact", "AUTHOR", false, "e.g. Patrick Merias")}
          {this._renderDateFormItem("meetingDate", "MEETING DATE", false)}
          {this._renderSelectFormItem("tdocStatus", "DOC STATUS", false, "e.g. Revised")}
          {this._renderSelectFormItem("specGroup", "SPECIFICATION GROUP", false, "e.g. SA2")}
          {this._renderSelectFormItem("specIPR", "INITIAL PLANNED RELEASE", false, "e.g. REL-8")}
          {this._renderSelectFormItem("specSeriesNo", "SPECIFICATION SERIES", false, "e.g. 26 - Codecs")}
          {this._renderSelectFormItem("specType", "SPECIFICATION TYPE", false, "e.g. TS")}
          {this._renderSelectFormItem("specNo", "SPECIFICATION NUMBER", false, "e.g. 36.133")}
        </Form>
      </Card>
    );
  }

  _renderSelectFormItem = (key, label, disabled, placeholder, options, valueRenderer, mode) => {
    const fieldES = {
      tdoc: "tdoc",
      source: "source",
      type: "type",
      for: "for",
      tdocStatus: "tdoc_status",
      meeting: "meeting",
      contact: "contact",
      standard: "spec_standard",
      technicalCategory: "is_user_equipment",
      specGroup: "spec_group",
      specIPR: "spec_initial_planned_release",
      specSeriesNo: "spec_series_no",
      specType: "spec_type",
      specNo: "spec_spec_no"
    }[key];
    return (
      <Form.Item label={label}>
        <MultiSelect
          value={this.state[key]}
          onChange={this._updateState(key)}
          options={options}
          optionApiCall={
            options
              ? undefined
              : val =>
                getDistinctValues(this.props.esUrl, fieldES, {
                  [fieldES]: val || ""
                })
          }
          optionRender={valueRenderer}
          placeholder={placeholder}
          disabled={disabled}
          mode={mode}
        />
      </Form.Item>
    );
  };

  _renderDateFormItem = (key, label, disabled) => {
    return (
      <Form.Item label={label}>
        <DateRangePicker value={this.state[key]} onChange={this._updateState(key)} disabled={disabled} />
      </Form.Item>
    );
  };

  _resetFilter = () => {
    this.setState(
      {
        fullText: undefined,
        tdoc: undefined,
        source: undefined,
        type: undefined,
        for: undefined,
        meetingDate: undefined,
        tdocStatus: undefined,
        meeting: undefined,
        contact: undefined,
        standard: undefined,
        technicalCategory: undefined,
        specGroup: undefined,
        specIPR: undefined,
        specSeriesNo: undefined,
        specType: undefined,
        specNo: undefined
      },
      this._updateRouter
    );
  };

  _runAfterTyping = (func, interval = 800) => {
    if (this._timeout) clearTimeout(this._timeout);
    this._timeout = setTimeout(func, interval);
  };

  _updateState = field => {
    return val => {
      this.setState({ [field]: val }, this._updateRouter);
    };
  };

  _updateRouter = () => {
    this.props.router.push({
      pathname: this.props.location.pathname,
      query: {
        full_text: this.state.fullText,
        tdoc: this.state.tdoc,
        source: this.state.source,
        type: this.state.type,
        for: this.state.for,
        meeting_date: this.state.meetingDate,
        tdoc_status: this.state.tdocStatus,
        meeting: this.state.meeting,
        contact: this.state.contact,
        spec_standard: this.state.standard,
        is_user_equipment: this.state.technicalCategory,
        spec_group: this.state.specGroup,
        spec_initial_planned_release: this.state.specIPR,
        spec_series_no: this.state.specSeriesNo,
        spec_type: this.state.specType,
        spec_spec_no: this.state.specNo
      }
    });
  };
}

export default withRouter(Toolbar3GPP);
